import React, { ReactNode } from 'react'
import { Badge, ItemType, Space } from 'src/antd'

import { TSidebarOriginalItem } from 'src/components/layout/types'
import {
  ApartmentOutlined,
  BarChartOutlined,
  BankOutlined,
  BulbOutlined,
  CalendarOutlined,
  CompassOutlined,
  DatabaseOutlined,
  NotificationOutlined,
  CustomerServiceOutlined,
  ContainerOutlined,
  TeamOutlined,
  SettingOutlined,
  DashboardOutlined,
  FileTextOutlined,
  TruckOutlined,
  MessageOutlined,
  FileDoneOutlined,
  MonitorOutlined,
  ShopOutlined,
  IdcardOutlined,
  DollarOutlined,
  WalletOutlined,
  ExperimentOutlined,
  ThunderboltOutlined,
} from 'src/icons/antd-compat'
import { routingMap } from 'src/reducers/routing/routing-map'
import { Role } from 'src/reducers/routing/utils'

const HAULER_HIDDEN_KEYS = [
  'dashboard',
  'insights',
  'customer_management',
  'asset_management',
  // PRO-177: Inventory left the Assets group for the top level — it stays
  // hidden for contractors exactly as it was while nested under Assets.
  'inventory',
  'invoicing',
  'notifications',
  // Hauler Mode (Erhverv) is an office module; contractor logins never
  // see it (the route is structural-only too — client hides, route
  // redirects, server denies).
  'hauler-mode',
  // Map Planning plots the office registries (containers + properties) that
  // are themselves hidden above — same office-only scope.
  'map-planning',
  // The server denies contractor logins both modules outright
  // (autopilot/schema.py `_deny_hauler_users`, route_studio/access.py), so
  // the entries only led to dead-end screens. Their routes are
  // structural-only too (main/route-config.tsx).
  'autopilot',
  'route-studio',
  // The automation engine is office-only (the backend gives hauler logins
  // nothing), and its route is structural-only too.
  'automations',
  // PRO-219: Fleet deliberately STAYS visible to contractors (product
  // call 2026-07-25) — the vehicles list is server-scoped to their own
  // hauler, and the section was kept when the role matrix was trimmed.
]

type GetItemsOptions = {
  /**
   * Whether the current contractor user can manage the contractor (manage
   * fleet/crew, propose structural changes). A Contractor Foreman cannot, so
   * manager-only workspace destinations are hidden from them (3.12.6). Defaults
   * to true so office users and any non-foreman path are unaffected.
   */
  canManageContractor?: boolean
  /**
   * Whether the current contractor user can manage their own crew (PRO-30):
   * holds any of the driver__create/edit/delete strings the BE's contractor
   * user-management lane rides on. Gates the workspace "Users" destination —
   * absent (not disabled) for foremen and drivers, exactly the accounts the
   * server denies. Defaults to false (fail-safe: the item never appears
   * unless the permission signal is explicitly passed).
   */
  canManageCrew?: boolean
  /**
   * Per-company Hauler-module opt-in (companySettings.haulerModuleEnabled).
   * OFF (the default, fail-safe): the menu is exactly the pre-build platform
   * menu — no office "Hauler" group, Routes back inside Operations, no
   * Complaints entry, no contractor workspace. ON: the current hauler-build
   * menu.
   */
  haulerModuleEnabled?: boolean
  /**
   * Per-company Hauler Mode (Erhverv commercial-waste module) opt-in
   * (companySettings.haulerB2bEnabled). OFF (the default, fail-safe):
   * the menu is exactly the pre-build menu. ON: one top-level Erhverv
   * destination (office users only). Distinct from haulerModuleEnabled.
   */
  haulerB2bEnabled?: boolean
  /**
   * Per-company Intelligence (insights) opt-in
   * (companySettings.insightsEnabled) — same pattern as invoicingEnabled.
   */
  insightsEnabled?: boolean
  /** Route Studio module flag (upstream feat) — gates its nav entry. */
  routeStudioEnabled?: boolean
  /**
   * Per-company Inventory (stock) module opt-in
   * (companySettings.inventoryEnabled, PRO-483). OFF (the default,
   * fail-safe): the Inventory nav entry stays hidden even on tenants with
   * the `inventory` tenant feature. Same single-source hook the route gate
   * reads (`useInventoryEnabled`), so discovery and access always agree.
   */
  inventoryEnabled?: boolean
  /**
   * Per-company Map Planning workspace opt-in
   * (companySettings.mapPlanningEnabled, COMPASS-1190). OFF (the default,
   * fail-safe): the Map Planning nav entry stays hidden. Same single-source
   * hook the route gate reads (`useMapPlanningEnabled`), so discovery and
   * access always agree.
   */
  mapPlanningEnabled?: boolean
  /**
   * Per-company legacy fill-level sensor Alerts opt-in
   * (companySettings.legacySensorAlerts, PRO-560). FAIL-OPEN: defaults to
   * `true` (shown) so the legacy top-level "Alerts" entry stays visible on
   * every backend until beperf's field ships and a company is explicitly
   * opted out — the same fail-open contract `useLegacySensorAlertsEnabled`
   * (the single-source hook the route gate reads) carries.
   */
  legacySensorAlertsEnabled?: boolean
  /**
   * Per-company "billing handled externally" mode
   * (companySettings.externalBillingEnabled, Visma & co). ON hides the
   * Invoicing entry even when `invoicingEnabled` is on: the company keeps the
   * invoicing backend for its integration but not the internal module. OFF
   * (the default, fail-safe) leaves the entry on `invoicingEnabled` alone.
   * Same pair `useInvoicingModuleVisible` resolves for the route gate.
   */
  externalBillingEnabled?: boolean
  /**
   * The automation engine's entry, resolved from `automationBootstrap`
   * (`automationEntryFor` in src/modules/automation/bootstrap.ts):
   * 'module' (enabled + canView) links the module, 'approvals' (enabled,
   * approver without canView) links straight to the approvals inbox, null
   * (the default, fail-closed) shows no entry.
   */
  automationEntry?: 'module' | 'approvals' | null
  /**
   * Count shown as a badge on the Automations entry (attention + pending
   * approvals, `useAutomationBadge`). 0 (the default) draws no badge.
   */
  automationBadge?: number
}

export default function getItems(
  t: (str: string, props?: Record<string, unknown>) => string,
  roles: Role[],
  onLinkClick: (pathname: string, state?: Record<string, unknown>) => void,
  invoicingEnabled: boolean,
  {
    canManageContractor = true,
    canManageCrew = false,
    haulerModuleEnabled = false,
    haulerB2bEnabled = false,
    insightsEnabled = false,
    routeStudioEnabled = false,
    inventoryEnabled = false,
    mapPlanningEnabled = false,
    legacySensorAlertsEnabled = true,
    externalBillingEnabled = false,
    automationEntry = null,
    automationBadge = 0,
  }: GetItemsOptions = {}
): ItemType[] {
  // Belt + braces: the `hauler` role is itself only granted when the module
  // flag is on (setRoles), but the workspace section also gates on the flag
  // directly so a stale role can never surface hauler UI with the flag off.
  const isHauler = roles.includes('hauler') && haulerModuleEnabled
  // A contractor foreman is a hauler user without management capability: they
  // get the day-ops workspace, minus the manager-only commercial/structural
  // destinations below.
  const isContractorForeman = isHauler && !canManageContractor

  const automationTitle =
    automationEntry === 'module' ? t('Automations') : t('Automation approvals')
  const automationLink =
    automationEntry === 'module'
      ? routingMap.app.automations._
      : routingMap.app.automations.approvals._
  const automationLabel: ReactNode =
    automationBadge > 0 ? (
      <Space>
        {automationTitle}
        <Badge count={automationBadge} size="small" />
      </Space>
    ) : (
      automationTitle
    )

  const items: TSidebarOriginalItem[] = [
    // Intelligence leads the menu: for opted-in companies it is the
    // module the platform opens on (see the /app/dashboard landing
    // redirect in main/route-config.tsx) — the menu order matches.
    ...(insightsEnabled
      ? [
          {
            key: 'insights',
            label: t('Intelligence'),
            icon: <BulbOutlined />,
            // Four verb-named destinations, not a nine-item wall. Each
            // owns its second-level tools via skipLinks (so it stays
            // highlighted on their sub-paths) and surfaces them as an
            // in-page tab strip (SectionNav.tsx) — the two lists must
            // stay in sync.
            children: [
              // Monitor first: its Overview is the module's (and the
              // platform's) landing page, so the menu leads with it.
              {
                title: t('Monitor'),
                icon: <BarChartOutlined />,
                link: routingMap.app.insights.overview._,
                skipLinks: [
                  routingMap.app.insights.dispatch._,
                  routingMap.app.insights.compliance._,
                  routingMap.app.insights.dashboards._,
                  routingMap.app.insights.alerts._,
                  routingMap.app.insights.haulers._,
                  routingMap.app.insights.drivers._,
                ],
                visibleInTooltip: true,
              },
              {
                title: t('Ask'),
                icon: <MessageOutlined />,
                link: routingMap.app.insights.analyst._,
                skipLinks: [
                  routingMap.app.insights.compare._,
                  routingMap.app.insights.explore._,
                ],
                visibleInTooltip: true,
              },
              {
                title: t('Report'),
                icon: <FileTextOutlined />,
                link: routingMap.app.insights.reports._,
                skipLinks: [routingMap.app.insights.digest._],
                visibleInTooltip: true,
              },
              {
                title: t('Trust'),
                icon: <FileDoneOutlined />,
                link: routingMap.app.insights.catalog._,
                skipLinks: [
                  routingMap.app.insights.audit._,
                  routingMap.app.insights.published._,
                ],
                visibleInTooltip: true,
              },
              // Iteration 2 of the sidemenu campaign: Data & Analytics is
              // one destination inside Intelligence — a tabbed page landing
              // on Dashboards (exports/imports are its in-page tabs).
              {
                title: t('Data & Analytics'),
                icon: <DashboardOutlined />,
                link: routingMap.app.analytics.dashboards._,
                skipLinks: [
                  routingMap.app.analytics.dashboards.create._,
                  routingMap.app.analytics.builder._,
                  routingMap.app.analytics.exports._,
                  routingMap.app.analytics['classic-exports']._,
                  routingMap.app.analytics.imports._,
                ],
                visibleInTooltip: true,
              },
            ],
          },
        ]
      : [
          // No Intelligence group to live under (insights off): Data &
          // Analytics stays a top-level direct link — same tabbed landing.
          // The legacy Metabase overview (/app/dashboard) left the menu but
          // its route stays reachable (it is still the app landing
          // redirect), so it rides skipLinks for highlight only.
          {
            key: 'dashboard',
            label: t('Data & Analytics'),
            icon: <DashboardOutlined />,
            link: routingMap.app.analytics.dashboards._,
            skipLinks: [
              routingMap.app.analytics.dashboards.create._,
              routingMap.app.analytics.builder._,
              routingMap.app.analytics.exports._,
              routingMap.app.analytics['classic-exports']._,
              routingMap.app.analytics.imports._,
              routingMap.app.dashboard._,
            ],
            onClick: () => onLinkClick(routingMap.app.analytics.dashboards._),
          },
        ]),
    // Iteration-2 order (product calls 2026-07-24, amended 2026-07-25):
    // Intelligence (with Data & Analytics inside), Tickets, Customers,
    // Route Studio, Live Operations (or Operations fallback), Hauler,
    // Inventory (with Assets inside), Fleet, Invoicing, Autopilot.
    // PRO-173: Tickets is a single direct-link entry — the in-page view
    // switcher (list · map · kanban) plus, since PRO-603, the TicketsSectionNav
    // strip (Tickets · Inbox) do the work the submenu used to do.
    {
      key: 'tickets',
      label: t('Tickets'),
      icon: <CustomerServiceOutlined />,
      link: routingMap.app.tickets._,
      skipLinks: [
        routingMap.app.tickets.kanban._,
        routingMap.app.tickets.map._,
        routingMap.app.tickets['create-bulk'].containers._,
        routingMap.app.tickets['create-bulk'].properties._,
        routingMap.app.tickets['customer-inbox']._,
        // Reached from the bulk-edit button on the list; without this the
        // Tickets entry loses its highlight there (PRO-603).
        routingMap.app.tickets['edit-bulk']._,
      ],
      onClick: () => onLinkClick(routingMap.app.tickets._),
    },
    // Iteration 2 follow-up (product call 2026-07-24): Customers is a
    // single direct-link entry — the modern-CRM shape: land on the primary
    // registry (Properties) with the sibling registries one in-page level
    // away (CustomersSectionNav strip: Properties · Groups · Contacts ·
    // Shared services · SMS/Email). The strip carries the gating the
    // submenu had — NEXUS-734's customer-registry gate on Shared services
    // lives there now, along with the legacyContacts/standaloneContacts tab
    // visibility (both show side by side when both flags are on). skipLinks
    // keep this entry highlighted across every module path (prefix-matched,
    // so create/detail/roles/compliance sub-paths are covered).
    {
      key: 'customer_management',
      label: t('Customers'),
      icon: <TeamOutlined />,
      link: routingMap.app['customer-management'].properties._,
      skipLinks: [
        routingMap.app['customer-management']['property-groups']._,
        routingMap.app['customer-management'].contacts._,
        routingMap.app['customer-management']['standalone-contacts']._,
        routingMap.app['customer-management']['shared-collection-points']._,
        routingMap.app['customer-management'].subscriptions._,
      ],
      onClick: () =>
        onLinkClick(routingMap.app['customer-management'].properties._),
    },
    // Route Studio is a top-level tab of its own (CEO placement call,
    // 2026-07-17, superseding the 2026-07-14 call that nested it under
    // Operations beside Route Schemes). URL stays /app/route-studio.
    // D01/D33: the company-settings flag remains for staged rollout.
    // Iteration-2 order: planning (Studio) reads before execution
    // (Operations).
    ...(routeStudioEnabled
      ? [
          {
            key: 'route-studio',
            label: t('Route Studio'),
            icon: <ExperimentOutlined />,
            link: routingMap.app['route-studio']._,
            // 2026-07-24 product call: with the module on, the Studio owns
            // the whole routing lifecycle — planning AND execution
            // (RouteStudioSectionNav: Studio · Schemes · Routes · Pickups ·
            // Weight Control) — so all their paths highlight
            // this entry.
            skipLinks: [
              routingMap.app['operation-management']['route-schemes']._,
              routingMap.app['operation-management'].routes._,
              routingMap.app['operation-management']['pickup-history']._,
              // COMPASS-1197: Truckloads rides the same strip.
              routingMap.app['operation-management'].truckloads._,
              routingMap.app['operation-management']['weight-control']._,
              // Task 06 (REQ 3.5.58, PRODUCT-83 M4): holiday impact rides
              // the same strip (RouteStudioSectionNav).
              routingMap.app['operation-management']['holiday-impact']._,
            ],
            onClick: () => onLinkClick(routingMap.app['route-studio']._),
          },
        ]
      : []),
    // Map Planning: the map-based planning workspace (containers +
    // properties on one shared map). Reads with the planning cluster —
    // directly after Route Studio, ahead of execution (Operations). A single
    // direct link; every path is /app/map-planning-prefixed so no skipLinks.
    // COMPASS-1190: gated on the per-company mapPlanningEnabled flag
    // (default OFF; staff switch it on per tenant from Control Center →
    // Feature configuration) — the Inventory / Route Studio staged-rollout
    // shape, so a flag-off tenant's menu reads exactly as before the page.
    ...(mapPlanningEnabled
      ? [
          {
            key: 'map-planning',
            label: t('Map Planning'),
            icon: <CompassOutlined />,
            link: routingMap.app['map-planning']._,
            onClick: () => onLinkClick(routingMap.app['map-planning']._),
          },
        ]
      : []),
    // Hauler Mode (Erhverv): ONE top-level destination like Invoicing/
    // Route Studio — the module's four destinations are tabs inside it
    // (S2-F1 consolidation, D-E9 max-4 law).
    // Flag-gated per company (fail-safe OFF; never unconditional — the
    // Autopilot precedent is explicitly not copied, INV-C §7 caution)
    // and stripped from contractor menus via HAULER_HIDDEN_KEYS.
    ...(haulerB2bEnabled
      ? [
          {
            key: 'hauler-mode',
            label: t('Erhverv'),
            icon: <ShopOutlined />,
            link: routingMap.app['hauler-mode']._,
            onClick: () => onLinkClick(routingMap.app['hauler-mode']._),
          },
        ]
      : []),
    // CORE platform operations entry (D26: core platform features never
    // live inside the module group). Two shapes (2026-07-24 product call):
    // with Route Studio on (office), execution moved into the Studio above,
    // so what remains is LIVE OPERATIONS standing alone — shown only to
    // role holders, like the tab it replaces. Module-off companies and
    // haulers keep the full Operations entry (in-page strip carries the
    // execution views), since they have no Studio to hold them.
    ...(() => {
      const ops = routingMap.app['operation-management']
      const hasLiveOpsRole = roles.includes('live-operations')

      if (routeStudioEnabled && !isHauler) {
        if (!hasLiveOpsRole) return []
        return [
          {
            key: 'operation_management',
            label: t('Live Operations'),
            icon: <MonitorOutlined />,
            link: ops['live-monitoring']._,
            onClick: () => onLinkClick(ops['live-monitoring']._),
          },
        ]
      }

      const operationsLanding = hasLiveOpsRole
        ? ops['live-monitoring']._
        : ops.routes._
      return [
        {
          key: 'operation_management',
          label: t('Operations'),
          icon: <SettingOutlined />,
          link: operationsLanding,
          skipLinks: [
            ops.routes._,
            ops['pickup-history']._,
            // COMPASS-1197: Truckloads rides the same strip.
            ops.truckloads._,
            ops['weight-control']._,
            ops['live-monitoring']._,
            ops['route-schemes']._,
            // Task 06 (REQ 3.5.58, PRODUCT-83 M4): module-off/hauler
            // variant (OperationsSectionNav) carries the same tab.
            ops['holiday-impact']._,
            // REQ 3.5.60 (PRO-642): same strip, live-operations gated.
            ops['driven-routes']._,
          ],
          onClick: () => onLinkClick(operationsLanding),
        },
      ]
    })(),
    // PRO-176: Fleet is a single direct-link entry — the in-page strip
    // switches Vehicles · Drivers · Locations · Planning. Positioned in the
    // execution cluster so it reads directly below Contractors when the
    // Hauler group injects (design call 2026-07-24 v2: Contractors → Fleet
    // → Inventory), and directly after Operations otherwise.
    {
      key: 'fleet_management',
      label: t('Fleet'),
      icon: <TruckOutlined />,
      link: routingMap.app['fleet-management'].vehicles._,
      skipLinks: [
        routingMap.app['fleet-management'].drivers._,
        routingMap.app['fleet-management'].locations._,
        routingMap.app['fleet-management']['vehicle-planner']._,
      ],
      onClick: () => onLinkClick(routingMap.app['fleet-management'].vehicles._),
    },
    // Assets is its own entry again (2026-07-29 product call, un-merging
    // the iteration-2 Inventory fold): the physical-asset registries —
    // Containers (landing) · Groups · Keys — as an in-page strip
    // (AssetsSectionNav). Hauler-hidden, like the group it replaces.
    {
      key: 'asset_management',
      label: t('Assets'),
      icon: <ContainerOutlined />,
      link: routingMap.app['asset-management'].containers._,
      skipLinks: [
        routingMap.app['asset-management']['container-group']._,
        routingMap.app['asset-management'].keys._,
      ],
      onClick: () =>
        onLinkClick(routingMap.app['asset-management'].containers._),
    },
    // Inventory lands on the consolidated workspace (PRO-368: rail + Overview
    // dashboard + the All-scope strip Overview · Stock Overview · Rental).
    // Its `inventory._` link prefix-matches every inventory route, so no
    // separate skipLink for it is needed. Rental's URL lives under
    // asset-management but belongs to this module, so it rides skipLinks to
    // keep highlighting here (prefix-matched, longer than the Assets link's).
    // PRO-483/PRO-561: gated solely on the per-company inventoryEnabled flag
    // (default OFF). The legacy per-hostname tenant gate was removed so a
    // company opted in via Django admin sees the module on every environment,
    // production included.
    ...(inventoryEnabled
      ? [
          {
            key: 'inventory',
            label: t('Inventory'),
            icon: <DatabaseOutlined />,
            link: routingMap.app.inventory._,
            skipLinks: [routingMap.app['asset-management'].rental._],
            onClick: () => onLinkClick(routingMap.app.inventory._),
          },
        ]
      : []),
    // Hidden for an external-billing company even with invoicing on: its
    // invoices are produced elsewhere, so the module is not a destination.
    ...(invoicingEnabled && !externalBillingEnabled
      ? [
          {
            key: 'invoicing',
            label: t('Invoicing'),
            icon: <FileDoneOutlined />,
            link: routingMap.app.invoicing._,
            onClick: () => onLinkClick(routingMap.app.invoicing._),
          },
        ]
      : []),
    // Autopilot closes the menu (2026-07-25 product call, reverting the
    // iteration-2 nesting under Tickets): automation is a back-office
    // concern, so it reads after Invoicing rather than among the work
    // queues. Direct link, all module paths are /app/autopilot-prefixed so
    // the entry needs no skipLinks.
    {
      key: 'autopilot',
      label: t('Autopilot'),
      icon: <ThunderboltOutlined />,
      link: routingMap.app.autopilot._,
      onClick: () => onLinkClick(routingMap.app.autopilot._),
    },
    // The automation engine, directly after Autopilot. An approver without
    // module access gets the same entry pointing at the approvals inbox.
    ...(automationEntry
      ? [
          {
            key: 'automations',
            // The badge sits inside the ReactNode label; `title` keeps the
            // text name for the collapsed rail (Menu.shadcn accessibleName).
            label: automationLabel,
            title: automationTitle,
            icon: <ApartmentOutlined />,
            link: automationLink,
            onClick: () => onLinkClick(automationLink),
          },
        ]
      : []),
    // Superseded for Intelligence companies: alerting lives under
    // Intelligence → Monitor (insights alerts) there, so the legacy
    // top-level entry is hidden to avoid two "Alerts" destinations.
    // Also gated on the per-company legacy-sensor-alerts opt-in (PRO-560,
    // fail-open): a company explicitly opted out loses the entry AND the
    // route (the notifications gate redirects). The route otherwise stays
    // reachable (deep links / non-opted-in companies keep the entry).
    ...(!insightsEnabled && legacySensorAlertsEnabled
      ? [
          {
            key: 'notifications',
            label: t('Alerts'),
            icon: <NotificationOutlined />,
            link: routingMap.app.notifications.alerts._,
            onClick: () => onLinkClick(routingMap.app.notifications.alerts._),
          },
        ]
      : []),
  ]

  // Dedicated CONTRACTOR (hauler-manager) workspace section (SPEC §4 W4,
  // decisions D11/D8). Shown ONLY to haulers, at the top, so the persona lands
  // in a coherent workspace. Tickets stays reachable from its existing
  // section, and the route list is the Operations entry's landing page —
  // PRO-262 removed the duplicate Workspace → "Route lists" child (one
  // destination, one entry); office-only concepts remain hidden
  // (HAULER_HIDDEN_KEYS).
  // PRO-36: no "My …" naming — several roles work inside a hauler company, so
  // possessives mislead. Labels are the plain destination names.
  const contractorWorkspaceItem: TSidebarOriginalItem = {
    key: 'contractor_workspace',
    label: t('Workspace'),
    icon: <ShopOutlined />,
    children: [
      {
        title: t('Overview'),
        icon: <DashboardOutlined />,
        link: routingMap.app.contractor.home._,
        visibleInTooltip: true,
      },
      {
        title: t('Company & fleet'),
        icon: <IdcardOutlined />,
        link: routingMap.app.contractor.company._,
        visibleInTooltip: true,
      },
      // Crew self-service (PRO-30): only for users holding the driver__*
      // strings the BE's contractor user-management lane checks — absent
      // (not disabled) for foremen and drivers, whom the server denies.
      ...(canManageCrew
        ? [
            {
              title: t('Users'),
              icon: <TeamOutlined />,
              link: routingMap.app.contractor.users._,
              visibleInTooltip: true,
            },
          ]
        : []),
      // Manager-only workspace destinations (3.12.6): proposing structural
      // changes is manager/office-only, and prices + settlement are commercial
      // surfaces. A foreman's workspace stays focused on day-ops, so these are
      // hidden from them (the server also denies the underlying writes).
      ...(isContractorForeman
        ? []
        : [
            {
              title: t('Proposals'),
              icon: <FileDoneOutlined />,
              link: routingMap.app.contractor.proposals._,
              visibleInTooltip: true,
            },
            {
              title: t('Prices'),
              icon: <DollarOutlined />,
              link: routingMap.app.contractor.prices._,
              visibleInTooltip: true,
            },
            {
              title: t('Settlement'),
              icon: <WalletOutlined />,
              link: routingMap.app.contractor.settlement._,
              visibleInTooltip: true,
            },
            {
              // PRO-466: the settlement report over an arbitrary period —
              // manager-only + hauler-scoped like Settlement above.
              title: t('Settlement report'),
              icon: <FileTextOutlined />,
              link: routingMap.app.contractor['settlement-report']._,
              visibleInTooltip: true,
            },
          ]),
      {
        title: t('Performance'),
        icon: <BarChartOutlined />,
        link: routingMap.app.contractor.performance._,
        visibleInTooltip: true,
      },
      {
        title: t('Feedback'),
        icon: <MessageOutlined />,
        link: routingMap.app.contractor.feedback._,
        visibleInTooltip: true,
      },
      // Task 06 (REQ 3.5.58, PRODUCT-83 M6): read-only twin, reachable by
      // every contractor login (operational, not commercial — plan F5).
      {
        title: t('Holiday impact'),
        icon: <CalendarOutlined />,
        link: routingMap.app.contractor['holiday-impact']._,
        visibleInTooltip: true,
      },
    ],
  }

  // PRO-175: Operations no longer has submenu children to strip — the
  // in-page OperationsSectionNav hides office-only tabs (Route Schemes,
  // Weight Control) and the role-gated Live Operations from haulers, so the
  // former officeOnlyOperationLinks filter moved there. The Operations entry
  // itself stays for haulers: it lands on Routes and, since PRO-262, is the
  // ONLY way in — the duplicate Workspace → "Route lists" child is gone.
  const visibleItems = isHauler
    ? [
        contractorWorkspaceItem,
        ...items.filter(
          (item) => 'type' in item || !HAULER_HIDDEN_KEYS.includes(item.key)
        ),
      ]
    : items

  // OFFICE "Hauler" section (PRO-66): exactly FOUR destinations, no
  // sub-headers — Overview (tabbed: Scorecards … Invoices · Settlement),
  // Contractors, Contract areas, Activities (Tasks · Proposals · Complaints ·
  // Unloading). Every view that used to be its own sidebar entry survives as
  // a tab, and the old URLs redirect (see operation-management route-config).
  // MODULE-NATIVE surfaces ONLY (D26): core platform features (e.g. Routes)
  // never live in here — they stay in the core Operations group above, which
  // is what makes removing the module (flag off) clean. Shown to office
  // (non-hauler) users only, and only when the company's Hauler module is
  // enabled — contractors keep their own workspace section.
  // Collapsed to a single direct-link entry (2026-07-24 product call,
  // completing the campaign pattern): "Contractors" lands on Overview and
  // the in-page ContractorsSectionNav strip carries the former subitems —
  // Overview · Contractors · Contract areas · Activities. Fleet stands as
  // its own entry directly below this one.
  const officeHaulerItem: ItemType = {
    key: 'office_hauler',
    label: t('Contractors'),
    icon: <BankOutlined />,
    onClick: () =>
      onLinkClick(routingMap.app['operation-management'].overview._),
    link: routingMap.app['operation-management'].overview._,
    skipLinks: [
      routingMap.app.settings.hauler.contractors._,
      routingMap.app.settings.hauler['contract-areas']._,
      // Coverage and its own-fleet flow, the Contract areas list's other view.
      routingMap.app.settings.hauler['contract-coverage']._,
      routingMap.app['operation-management'].activities._,
    ],
  } as ItemType

  const mappedItems = visibleItems.map((item) => {
    if ('type' in item) {
      return item
    }

    if (item.onClick || item.link) {
      return {
        key: item.key,
        label: item.label,
        // a ReactNode label (the Automations badge) keeps a text name here
        ...(item.title ? { title: item.title } : {}),
        icon: item.icon,
        onClick: item.onClick,
        // Preserved for findBestMatchKey (same as the child mapping below):
        // collapsed former groups have keys that are NOT substrings of their
        // route paths (fleet_management vs /app/fleet-management), so the
        // highlight needs the real link and its owned sub-paths.
        link: item.link,
        skipLinks: item.skipLinks,
      }
    }

    if (!item.children) {
      return {
        key: item.key,
        label: item.label,
        icon: item.icon,
      }
    }

    return {
      key: item.key,
      label: item.label,
      icon: item.icon,
      children: item.children.map(
        (child) =>
          ({
            key: child.link,
            label: child.title,
            icon: child.icon,
            // antd MenuItem hover title; defaults to the label otherwise.
            ...(child.tooltip ? { title: child.tooltip } : {}),
            onClick: () => onLinkClick(child.link, child.linkState),
            // Preserved for findBestMatchKey so a destination that owns
            // second-level sub-paths (skipLinks) stays highlighted while
            // the user is on one of them — the collapsed-verb nav relies
            // on this.
            link: child.link,
            skipLinks: child.skipLinks,
          } as ItemType)
      ),
    }
  })

  // Contractors keep their own workspace and never see the office section.
  if (isHauler) return mappedItems

  // With the module off the menu is the pre-build platform menu, untouched.
  if (!haulerModuleEnabled) return mappedItems

  // Office + module on: inject the Hauler group after the ops entry
  // (iteration-2 order: … Route Studio, Map Planning, Live Operations/
  // Operations, Hauler, Inventory …; D26 — never above Data & Analytics).
  // The ops entry is role-hidden for non-live-ops users when Route Studio
  // is on, so the anchor falls back along the planning cluster: Map
  // Planning — flag-gated since COMPASS-1190, so it may be absent as well —
  // then Route Studio, which is present by construction whenever the ops
  // entry was dropped (that drop only happens with the Studio on). Either
  // way Contractors lands directly above Fleet (design call 2026-07-24 v2);
  // getItems.order.test.tsx pins the flag-off shape. Should every anchor
  // ever be missing, the group leads the menu — gate the Studio's ENTRY
  // separately, or slot a planning item between Studio and Map Planning,
  // and that fallback becomes live.
  const anchorIndex = ['operation_management', 'map-planning', 'route-studio']
    .map((key) =>
      mappedItems.findIndex((item) => item && 'key' in item && item.key === key)
    )
    .find((index) => index !== -1)
  const insertAt = anchorIndex === undefined ? 0 : anchorIndex + 1
  return [
    ...mappedItems.slice(0, insertAt),
    officeHaulerItem,
    ...mappedItems.slice(insertAt),
  ]
}
